import {
  errorSummary,
  errorDetails,
  isAndroidPlatform,
  isIosPlatform,
  makeModelLabel,
  undecodedChannelsNote,
} from "./format.ts";
import {
  classifyErrorKind,
  codeplugParams,
  errorTypeName,
  firstIssueColumn,
  radioEventParams,
  trackEvent,
} from "./analytics.ts";
import { FLOWS, OUTCOMES, recordFlow } from "./metrics.ts";
import {
  PORT_SELECTION_CANCELLED_MESSAGE,
  isPortSelectionCancelled,
  isSerialUnsupported,
} from "../serial-errors.ts";
import { PORT_LOST_REASONS } from "../serial-bridge.ts";
import { requireRuntimeApi } from "./state.ts";
import type { UiContext } from "../types/ui-context.js";
import type { CatalogRadio, RowIssue, SettingIssue } from "../runtime-rpc.ts";
import type { RadioSessionHandle } from "./state.ts";

// How the status line words a loss the bridge caused itself, by the reason it
// reports (PORT_LOST_REASONS); any other loss reads as a disconnect, which is
// what it is to the user whether the adapter went or its stream stopped.
const PORT_LOST_CAUSES: Readonly<Record<string, string>> = Object.freeze({
  [PORT_LOST_REASONS.BAUD_RATE_CHANGE]: "Serial port closed while switching to the selected radio's baud rate",
  [PORT_LOST_REASONS.RECONFIGURE]: "Serial port closed after the radio's driver changed its settings",
});

const LIVE_RADIO_TITLE = "Live-mode radios are not supported in this UI yet";
const NO_RADIO_SELECTED_TITLE = "Search for and select a radio first";
const CLONE_RUNNING_TITLE = "A radio transfer is already running";
// Shown when a clone is started on a browser without WebAssembly stack
// switching: CHIRP's clone loops block on the serial bridge through Pyodide's
// run_sync, which is the one thing in the app that needs JSPI.
const CLONE_UNSUPPORTED_MESSAGE =
  "This browser cannot program radios: it lacks WebAssembly stack switching (JSPI), "
  + "which the radio download/upload needs. Recent Chrome, Edge and Firefox support it. "
  + "Editing CSV and image files still works here.";

// Everything on the serial path: connect/disconnect over Web Serial, WebUSB or BLE,
// the enabled/visible state of the sidebar's radio actions, the clone progress
// bar, and the download/upload clone operations with their preflight. Owns the
// connection and capability state.
// Which flow and outcome each clone event stands for. Keyed by the GA event
// name rather than derived from it, so "radio_download_success" is still one
// grep away from every place it is reported.
const CLONE_FLOW_OUTCOMES: Readonly<Record<string, { flow: string; outcome: string }>> = Object.freeze({
  radio_download_success: { flow: FLOWS.RADIO_DOWNLOAD, outcome: OUTCOMES.OK },
  radio_download_failure: { flow: FLOWS.RADIO_DOWNLOAD, outcome: OUTCOMES.FAILED },
  radio_upload_success: { flow: FLOWS.RADIO_UPLOAD, outcome: OUTCOMES.OK },
  radio_upload_failure: { flow: FLOWS.RADIO_UPLOAD, outcome: OUTCOMES.FAILED },
});

/** What the preflight before an upload or a binary export found. */
export interface PreflightResult {
  valid: boolean;
  // The selected radio changed while the check ran: nothing was shown, and
  // the write it guarded must not go ahead.
  stale: boolean;
  issues: Array<RowIssue | SettingIssue>;
  // "2 invalid value(s) highlighted in red in channels", or "" with none.
  summary: string;
}

// Say how many values the preflight highlighted and in which view, so a
// blocked write sends the user to the view that holds them: a settings value
// is never in the channel grid, whichever view is open.
function preflightSummary(rowIssues: number, settingIssues: number): string {
  if (rowIssues > 0 && settingIssues > 0) {
    return `${rowIssues} invalid value(s) highlighted in red in channels and ${settingIssues} in radio settings`;
  }
  if (rowIssues > 0) {
    return `${rowIssues} invalid value(s) highlighted in red in channels`;
  }
  if (settingIssues > 0) {
    return `${settingIssues} invalid value(s) highlighted in red in radio settings`;
  }
  return "";
}

export function createSerialActions(ctx: UiContext) {
  const { dom, state, log } = ctx;

  let transportController: { capability?: typeof capability; choosePort(transport: string): Promise<void> } | null = null;
  let capability = { supported: false, native: false, webusb: false, webbluetooth: false };
  // Whether this browser can run a clone at all (WebAssembly JSPI). Checked
  // when a connection is started, not at init: the rest of the app works
  // without it and the user only needs to hear about it when it matters.
  let cloneSupported = true;
  let sidebarControlsEnabled = false;
  let connected = false;
  // Transport of the active connection, used to show one Disconnect button
  // while any serial transport is connected.
  let transport = "";
  // Whether a download or upload is in flight, preflight included. Runtime
  // calls run one at a time (enqueueRuntimeCall in web/js/runtime-rpc.ts), so
  // nothing pressed meanwhile runs beside the transfer: it waits behind it.
  // A second Download or Upload would start another transfer the moment this
  // one ended, usually by timing out; a Disconnect could not interrupt it,
  // only close the port after it; and a Connect after a port loss would open
  // its chooser once the user's click had expired. So every serial control
  // waits for the transfer to end, and so does the radio search: a radio
  // picked mid-transfer leaves the transfer's result without the radio it ran
  // against. A lost port still ends it promptly: the bridge fails every read
  // once the port is gone.
  let cloneRunning = false;
  // A connect in flight, and a loss the bridge reported during it. The bridge
  // reports a loss as soon as it happens, while the connect's own result is
  // still on its way back through the runtime; dropped as "not connected",
  // that report was overtaken by the result marking the lost port connected.
  // It is held instead and applied once the result has been.
  let connecting = false;
  let lossDuringConnect: { deviceName?: string; reason?: string } | null = null;

  // Hand over the loss held during a connect, if any, and forget it. A
  // function rather than a read in place: handlePortLost() sets it while the
  // connect is awaiting, which narrowing inside connectSerial() cannot see.
  function takeLossDuringConnect(): { deviceName?: string; reason?: string } | null {
    const loss = lossDuringConnect;
    lossDuringConnect = null;
    return loss;
  }

  // Wire the serial bridge's transport controls (capability + the chooser for
  // a forced transport) so the UI can offer explicit WebUSB and WebBluetooth
  // connect paths.
  function setSerialController(controller: NonNullable<typeof transportController> | null): void {
    transportController = controller || null;
    capability = controller?.capability || capability;
    updateSerialActionState();
  }

  function setSerialButtonsBusy(busy: boolean): void {
    dom.serialConnectToggleEl.disabled = busy;
    dom.webusbConnectToggleEl.disabled = busy;
    dom.webbluetoothConnectToggleEl.disabled = busy;
  }

  // Mark a transfer as started or finished and repaint the serial controls,
  // so nothing can be queued behind a running transfer (see cloneRunning).
  function setCloneRunning(running: boolean): void {
    cloneRunning = running;
    updateSerialActionState();
  }

  // Whether a download or upload is in flight (see cloneRunning).
  function isCloneRunning(): boolean {
    return cloneRunning;
  }

  // Record whether a clone can run here at all; web/app.ts decides from the
  // WebAssembly feature check and web/js/ui.ts passes it through init().
  function setCloneSupported(supported: boolean): void {
    cloneSupported = Boolean(supported);
  }

  // Connect using the requested transport ("auto", "webusb" or "webbluetooth").
  async function connectSerial(preferredTransport: string): Promise<void> {
    // A transfer still running after a port loss holds the toggles: a connect
    // would only wait behind it in the runtime queue, saying "Connecting...".
    if (connected || cloneRunning) {
      return;
    }
    if (!cloneSupported) {
      log.setStatus(CLONE_UNSUPPORTED_MESSAGE);
      log.logSerial(CLONE_UNSUPPORTED_MESSAGE);
      return;
    }
    setSerialButtonsBusy(true);
    connecting = true;
    lossDuringConnect = null;
    try {
      const baudRate = Number(state.selectedRadio?.baudRate || 9600);
      const transportLabel = preferredTransport === "webbluetooth"
        ? " via WebBluetooth"
        : (preferredTransport === "webusb" ? " via WebUSB" : "");
      log.setStatus(`Connecting serial${transportLabel}...`);
      // Before anything here awaits: the chooser needs this click's user
      // activation, which can lapse while serialConnect waits on the runtime.
      await transportController?.choosePort(preferredTransport);
      const result = await requireRuntimeApi(state).serialConnect({ baudRate });
      connected = Boolean(result?.connected);
      if (result?.deviceName) {
        log.logDebug(`SERIAL DEVICE ${result.deviceName}`);
      }
      transport = result?.transport || "";
      if (result?.transport) {
        log.logSerial(`Transport: ${result.transport}`);
      }
      if (result?.usbVendorId) {
        state.lastUsbVendorId = result.usbVendorId;
      }
      if (result?.usbProductId) {
        state.lastUsbProductId = result.usbProductId;
      }
      if (state.lastUsbVendorId || state.lastUsbProductId) {
        log.logDebug(`SERIAL USB ID ${state.lastUsbVendorId || "unknown"}:${state.lastUsbProductId || "unknown"}`);
      }
      log.setStatus(result.message || "Serial connected.");
      trackEvent("serial_connected", {
        ...radioEventParams(state.selectedRadio),
        transport: transport || "unknown",
      });
      recordFlow(FLOWS.SERIAL_CONNECT, OUTCOMES.OK, {
        ...radioEventParams(state.selectedRadio),
        transport: transport || "unknown",
      });
      // The connect did succeed; the port went after it. Said in that order.
      const loss = takeLossDuringConnect();
      if (loss) {
        handlePortLost(loss.deviceName, loss.reason);
      }
    } catch (error) {
      // A user who closes the browser's port picker lands here too; error_kind
      // separates that from an adapter the browser could not open.
      const errorKind = classifyErrorKind(error);
      const errorType = errorTypeName(error);
      trackEvent("serial_connect_failed", {
        ...radioEventParams(state.selectedRadio),
        error_kind: errorKind,
        error_type: errorType,
      });
      // Dismissing the chooser is the one outcome here the user already knows
      // about, so it gets a sentence rather than the Pyodide traceback the
      // failure path dumps. It still has to be said out loud: with no visible
      // status surface in this app, saying nothing left Connect-then-Cancel
      // looking exactly like a button that does not work.
      //
      // It is also not a failed connect, and must not be recorded as one:
      // closing the picker is the commonest way out of this dialog, so counting
      // it would swamp the failure rate the metric exists to expose with the
      // one outcome that is nothing going wrong. Same call the debug log makes
      // through reportActionCancelled, and the same reasoning that leaves a
      // cancelled codeplug import unrecorded. GA still gets the event, where
      // error_kind separates the two.
      if (isPortSelectionCancelled(error)) {
        log.reportActionCancelled("Serial connect", PORT_SELECTION_CANCELLED_MESSAGE);
        return;
      }
      // A missing browser API is a capability limit, not a failed adapter.
      // Forced transport calls can still reach this path despite UI gating.
      if (isSerialUnsupported(error)) {
        log.setStatus("This browser does not support the selected serial transport. Editing CSV and image files still works here.");
        log.logSerial(errorDetails(error));
        return;
      }
      recordFlow(FLOWS.SERIAL_CONNECT, OUTCOMES.FAILED, {
        ...radioEventParams(state.selectedRadio),
        // What was being attempted, since nothing was negotiated. Without it a
        // dashboard grouped by transport counts explicit transports' successes and drops
        // its failures, which reads as a success rate far better than the real
        // one. "auto" is an honest fallback value: the browser was left to choose
        // and the attempt never got far enough to say what it would have
        // chosen.
        transport: preferredTransport || "auto",
        error_kind: errorKind,
        error_type: errorType,
      });
      log.reportActionError("Serial connect", error);
      log.logSerial(`ERROR ${errorSummary(error)}`);
    } finally {
      connecting = false;
      lossDuringConnect = null;
      setSerialButtonsBusy(false);
      refreshSerialConnectToggleLabel();
      updateSerialActionState();
    }
  }

  async function disconnectSerial() {
    // Disconnect would only wait behind a running transfer, not stop it.
    if (cloneRunning) {
      return;
    }
    setSerialButtonsBusy(true);
    try {
      log.setStatus("Disconnecting serial...");
      const result = await requireRuntimeApi(state).serialDisconnect();
      connected = Boolean(result?.connected);
      if (!connected) {
        transport = "";
      }
      log.setStatus(result.message || "Serial disconnected.");
    } catch (error) {
      log.reportActionError("Serial disconnect", error);
      log.logSerial(`ERROR ${errorSummary(error)}`);
    } finally {
      setSerialButtonsBusy(false);
      refreshSerialConnectToggleLabel();
      updateSerialActionState();
    }
  }

  // The open port is gone: unplugged, powered down with the radio on cables
  // that draw from it, or refused when the bridge reopened it for a driver with
  // a different baud rate or for a driver's mid-clone settings change. The
  // bridge has already let go of the port, so the UI's job is to stop claiming
  // there is a connection — otherwise Download/Upload stay lit against a port
  // that is gone.
  function handlePortLost(deviceName: string | undefined, reason: string | undefined): void {
    if (!connected) {
      if (connecting) {
        lossDuringConnect = { deviceName, reason };
      }
      return;
    }
    const lostTransport = transport || "unknown";
    connected = false;
    transport = "";
    const cause = PORT_LOST_CAUSES[reason || ""] || "Serial port disconnected";
    log.setStatus(`${cause}; reconnect to continue.`);
    log.logSerial(`${cause}${deviceName ? ` (${deviceName})` : ""}.`);
    trackEvent("serial_port_lost", {
      ...radioEventParams(state.selectedRadio),
      transport: lostTransport,
    });
    refreshSerialConnectToggleLabel();
    updateSerialActionState();
  }

  function setSidebarControlsEnabled(enabled: boolean): void {
    sidebarControlsEnabled = Boolean(enabled);
    for (const el of dom.sidebarControlEls) {
      el.disabled = !enabled;
    }
    updateSerialActionState();
  }

  // The unsupported-browser treatment is two synchronized pieces: the
  // explanation overlay and the greyscale on the app shell behind it. Toggle
  // them together so the page never ends up grey with no explanation (or the
  // reverse). `problems` picks which explanation the card shows. iOS/iPadOS is
  // the exception: whatever the capability gap is there, it is the platform
  // rather than the browser choice, so its block replaces the generic one
  // instead of telling people to install a browser that would be the same
  // WebKit. Missing WebAssembly stack switching is deliberately not an overlay
  // any more: it only stops a clone, and connectSerial() says so when one is
  // started.
  function setBrowserUnsupportedOverlayVisible(visible: boolean, problems: { serial?: boolean } = { serial: true }): void {
    const show = Boolean(visible);
    const iosShown = isIosPlatform();
    const serialShown = !iosShown && Boolean(problems.serial);
    dom.unsupportedBrowserIosInfoEl.hidden = !iosShown;
    dom.unsupportedBrowserSerialInfoEl.hidden = !serialShown;
    const labelledBy = iosShown
      ? "unsupported-browser-ios-title"
      : "unsupported-browser-serial-title";
    dom.unsupportedBrowserOverlayEl.setAttribute("aria-labelledby", labelledBy);
    dom.unsupportedBrowserOverlayEl.classList.toggle("hidden", !show);
    dom.appShellEl.classList.toggle("browser-unsupported", show);
  }

  function setLiveRadioSupportWarningVisible(visible: boolean): void {
    dom.liveRadioSupportWarningEl.hidden = !visible;
  }

  function refreshSerialConnectToggleLabel() {
    // Mobile has no hover tooltips, so on Android the labels themselves say
    // what each transport is for.
    const mobile = isAndroidPlatform();
    dom.serialConnectToggleEl.textContent = connected
      ? "Disconnect"
      : (mobile ? "Connect via WebSerial (Bluetooth)" : "Connect via WebSerial");
    dom.webusbConnectToggleEl.textContent = connected
      ? "Disconnect"
      : (mobile ? "Connect via WebUSB (wired adapter)" : "Connect via WebUSB");
    dom.webbluetoothConnectToggleEl.textContent = connected
      ? "Disconnect"
      : "Connect via WebBluetooth";
  }

  // Show the clone progress bar in its indeterminate state until the driver's
  // first status report arrives with real block counts.
  function beginCloneProgress(label: string): void {
    dom.cloneProgressLabelEl.textContent = String(label || "Working...");
    dom.cloneProgressPercentEl.textContent = "";
    dom.cloneProgressBarEl.removeAttribute?.("value");
    dom.cloneProgressEl.hidden = false;
  }

  // CHIRP drivers report status once per transferred block (cur/max may be -1
  // when a driver reports no counts; the bar then stays indeterminate).
  function updateCloneProgress(cur: number, max: number, msg: string): void {
    dom.cloneProgressEl.hidden = false;
    if (msg) {
      dom.cloneProgressLabelEl.textContent = msg;
    }
    if (Number.isFinite(cur) && Number.isFinite(max) && max > 0 && cur >= 0) {
      const percent = Math.max(0, Math.min(100, Math.round((cur / max) * 100)));
      dom.cloneProgressBarEl.value = percent;
      dom.cloneProgressPercentEl.textContent = `${percent}%`;
    } else {
      // A no-count report must not leave the previous phase's percentage on
      // screen: removing value makes the <progress> bar indeterminate again.
      dom.cloneProgressBarEl.removeAttribute?.("value");
      dom.cloneProgressPercentEl.textContent = "";
    }
  }

  function endCloneProgress() {
    dom.cloneProgressEl.hidden = true;
  }

  function selectedRadioIsLiveMode() {
    return Boolean(state.selectedRadio?.isLiveRadio);
  }

  function updateSerialActionState() {
    const liveRadioUnsupported = selectedRadioIsLiveMode();
    // Radios are chosen by searching, so a fresh session has none selected and
    // every serial action has to explain that rather than act on nothing.
    const noRadioSelected = !state.selectedRadio;
    const actionsAllowed =
      sidebarControlsEnabled && !liveRadioUnsupported && !noRadioSelected
      && (connected || capability.supported);
    // Why the radio itself blocks these controls, if it does. Checked before
    // the connection state so the buttons name the first thing to fix.
    const selectionBlockedTitle = noRadioSelected
      ? NO_RADIO_SELECTED_TITLE
      : (liveRadioUnsupported ? LIVE_RADIO_TITLE : "");
    // A running transfer outranks every other reason: it is why nothing here
    // answers right now, and it clears by itself.
    const blockedTitle = (cloneRunning ? CLONE_RUNNING_TITLE : "") || selectionBlockedTitle;
    const togglesAllowed = actionsAllowed && !cloneRunning;

    setLiveRadioSupportWarningVisible(liveRadioUnsupported);

    // Painted here rather than once by setCloneRunning(), like the buttons
    // below, so no repaint mid-transfer can hand the search back early.
    dom.radioSearchEl.disabled = !sidebarControlsEnabled || cloneRunning;
    dom.radioSearchEl.title = cloneRunning ? CLONE_RUNNING_TITLE : "";

    // Connect controls by platform capability:
    // - Desktop with native Web Serial: WebSerial toggle.
    // - Android with native Web Serial (Bluetooth RFCOMM serial ports): both
    //   toggles — WebSerial for Bluetooth serial, WebUSB for wired USB
    //   adapters, which Android's native Web Serial cannot drive.
    // - WebUSB-only browsers (older Android Chrome): WebUSB toggle only.
    // - WebBluetooth adds a separate BLE dongle toggle on supported browsers.
    // - No transport API: the WebSerial toggle stays visible (disabled) alongside
    //   the unsupported-browser warning.
    const webusbOnly = capability.webusb && !capability.native;
    let showWebSerialToggle = !webusbOnly && (capability.native || !capability.webbluetooth);
    let showWebUsbToggle =
      capability.webusb && (!capability.native || isAndroidPlatform());
    let showWebBluetoothToggle = Boolean(capability.webbluetooth);
    // While connected, collapse to a single Disconnect button on the toggle
    // matching the active transport.
    if (connected) {
      showWebUsbToggle = transport === "webusb";
      showWebBluetoothToggle = transport === "webbluetooth";
      showWebSerialToggle = !showWebUsbToggle && !showWebBluetoothToggle;
    }

    dom.serialConnectToggleEl.hidden = !showWebSerialToggle;
    dom.serialConnectToggleEl.disabled = !togglesAllowed;
    dom.serialConnectToggleEl.title = blockedTitle
      || (!capability.supported ? "This browser has no supported serial transport API" : "")
      || (isAndroidPlatform()
        ? "Connect over native Web Serial, for use with Bluetooth serial ports"
        : "");

    dom.webusbConnectToggleEl.hidden = !showWebUsbToggle;
    dom.webusbConnectToggleEl.disabled = !togglesAllowed;
    dom.webusbConnectToggleEl.title = blockedTitle
      || "Connect over WebUSB, for use with FTDI, Prolific PL2303, "
        + "WCH CH340/CH341 or Silicon Labs CP2102 adapters";

    dom.webbluetoothConnectToggleEl.hidden = !showWebBluetoothToggle;
    dom.webbluetoothConnectToggleEl.disabled = !togglesAllowed;
    dom.webbluetoothConnectToggleEl.title = blockedTitle
      || "Connect over WebBluetooth to a BT-1AD programming dongle used with Ola Radio";

    // Both clone operations talk to an open port, so neither is offered until
    // a port has been picked and opened through one of the connect buttons,
    // nor while either of them is already running.
    const cloneAllowed = togglesAllowed && connected;
    const notConnectedTitle = "Connect to a serial port first";

    dom.radioDownloadEl.disabled = !cloneAllowed;
    dom.radioDownloadEl.title = blockedTitle
      || (connected ? "" : notConnectedTitle);

    dom.radioUploadEl.disabled = !cloneAllowed || ctx.settings.hasInvalidSettings();
    if (blockedTitle) {
      dom.radioUploadEl.title = blockedTitle;
      return;
    }
    if (!connected) {
      dom.radioUploadEl.title = notConnectedTitle;
      return;
    }
    dom.radioUploadEl.title = ctx.settings.hasInvalidSettings()
      ? "Fix invalid radio settings before upload"
      : "";
  }

  function trackRadioEvent(eventName: string, radio: CatalogRadio | null, params: Record<string, unknown> = {}): void {
    if (!radio) {
      return;
    }
    trackEvent(eventName, { ...radioEventParams(radio), ...params });
  }

  // Report how a clone ended. The attempt events on their own only count who
  // pressed the button; pairing them with an outcome is what turns the reports
  // into a per-driver record of which radios actually work in the browser.
  //
  // The same outcome goes to Sentry as a flow metric. GA answers this over
  // days, from a property someone has to go and read; the metric answers it as
  // a rate a dashboard can watch and an alert can fire on, which is what a
  // driver that breaks in a new browser release needs.
  //
  // Every outcome carries the transport the clone ran over, successes as well
  // as failures, because the rate is what reads it. Sentry drops the events of
  // a driver's own clone failures (isDriverCloneFailure, web/js/sentry.ts), so
  // a transport that loses bytes shows only here: as a failure rate that moves
  // with the transport across every driver. It is the one the clone started
  // with, passed in by the caller, because a port lost mid-clone clears the
  // connection's transport before the failure arrives.
  function trackCloneOutcome(
    eventName: string,
    radio: CatalogRadio | null,
    startedAt: number,
    cloneTransport: string,
    params: Record<string, unknown> = {},
  ): void {
    const durationMs = Date.now() - startedAt;
    const outcomeParams = { transport: cloneTransport || "unknown", ...params };
    trackRadioEvent(eventName, radio, {
      duration_ms: durationMs,
      ...outcomeParams,
    });
    const dimensions = CLONE_FLOW_OUTCOMES[eventName];
    if (!radio || !dimensions) {
      return;
    }
    recordFlow(
      dimensions.flow,
      dimensions.outcome,
      { ...radioEventParams(radio), ...outcomeParams },
      durationMs,
    );
  }

  function cloneFailureParams(error: unknown, stage: string) {
    return {
      stage,
      error_kind: classifyErrorKind(error),
      error_type: errorTypeName(error),
    };
  }

  // Validate rows and radio-wide settings against session's radio before a
  // write, highlighting every rejected value so the user can see what blocked
  // it. An upload runs it, and so does a binary export (codeplug-io), which
  // writes the same rows into an image and would refuse the same values. The
  // caller passes the session it will write to: a radio picked while the check
  // runs makes the answer stale, and a stale answer is neither shown nor
  // acted on. summary says how many values are highlighted and where.
  async function runUploadPreflight(session: RadioSessionHandle | null): Promise<PreflightResult> {
    if (!state.selectedRadio) {
      return {
        valid: false,
        stale: false,
        issues: [{ rowIndex: -1, column: "", message: "No radio selected." }],
        summary: "",
      };
    }
    const sessionId = await ctx.session.idOf(session);
    const [rowResult, settingsResult] = await Promise.all([
      requireRuntimeApi(state).validateRowsForUpload({
        rows: state.currentRows,
        sessionId,
      }),
      requireRuntimeApi(state).validateRadioSettings({
        settings: ctx.settings.getGroups(),
        sessionId,
      }),
    ]);
    if (!ctx.session.isCurrent(session)) {
      log.logDebug("PREFLIGHT discarded: the selected radio changed while it ran.");
      return { valid: false, stale: true, issues: [], summary: "" };
    }
    const result = rowResult;
    const settingsValidation = settingsResult
      || { valid: true, issues: [], settings: ctx.settings.getGroups() };
    // An unavailable reply says the runtime could not read settings, not that
    // the radio has none: keep the panel's tree, and say why in Debug Output.
    if (settingsResult && settingsResult.available === false) {
      if (ctx.settings.getGroups().length > 0) {
        log.logDebug(
          `PREFLIGHT settings unavailable: ${settingsResult.message || "no reason given"}`
            + (settingsResult.error ? ` (${settingsResult.error})` : ""),
        );
      }
    } else {
      ctx.settings.setGroups(settingsValidation.settings);
    }
    ctx.settings.ensureActiveTab();
    ctx.settings.clearInvalid();
    ctx.settings.applyValidationIssues(settingsValidation.issues);
    ctx.settings.updateSummary();
    ctx.table.clearInvalidHighlights();
    const issues = Array.isArray(result?.issues) ? result.issues : [];
    const warnings = Array.isArray(result?.warnings) ? result.warnings : [];
    for (const warning of warnings) {
      const rowIdx = Number(warning?.rowIndex);
      const channel = state.currentRows[rowIdx]?.Location ?? rowIdx;
      log.logDebug(
        `PREFLIGHT WARNING channel=${channel} column=${warning?.column || "unknown"}: ${warning?.message || "Driver warning"}`,
      );
    }
    ctx.table.applyValidationIssues(issues);
    if (issues.length > 0) {
      ctx.table.render();
    }
    ctx.settings.render();
    const settingIssues = settingsValidation.issues || [];
    return {
      valid: Boolean(result?.valid)
        && Boolean(settingsValidation?.valid)
        && ctx.settings.invalidCount() === 0,
      stale: false,
      issues: [...issues, ...settingIssues],
      summary: preflightSummary(issues.length, ctx.settings.invalidCount()),
    };
  }

  // Re-read the selected radio's column metadata now that its session holds
  // a clone image. Guarded twice over, because a clone runs long enough for
  // the user to move on: a session that is no longer current belongs to a
  // different radio's schema, and a metadata call that fails leaves the grid
  // on the schema it already had -- neither is a reason to report the
  // transfer that just succeeded as a failure, so both only reach the debug
  // panel.
  async function refreshMetadataForDownloadedRadio(session: RadioSessionHandle | null): Promise<void> {
    if (!ctx.session.isCurrent(session)) {
      return;
    }
    try {
      await ctx.catalog.loadSelectedRadioMetadata();
    } catch (error) {
      log.logDebug(`RADIO METADATA refresh after download failed: ${errorSummary(error)}`);
    }
  }

  async function downloadFromRadio() {
    // The button is disabled while a transfer runs; this covers a press that
    // reaches the handler anyway, and is neither reported nor queued.
    if (cloneRunning) {
      return;
    }
    if (!state.selectedRadio) {
      log.setStatus("Search for and select a radio first.");
      return;
    }
    // Captured up front: a clone runs long enough for the user to pick a
    // different radio while it is in flight, and the outcome belongs to the
    // radio -- and the session -- the transfer actually ran against.
    const radio = state.selectedRadio;
    const session = ctx.session.current();
    const startedAt = Date.now();
    // As is the transport, which a port lost mid-clone clears (handlePortLost).
    const cloneTransport = transport;
    setCloneRunning(true);
    try {
      trackRadioEvent("radio_download", radio);
      log.setStatus(`Downloading from ${makeModelLabel(radio)}...`);
      beginCloneProgress(`Downloading from ${makeModelLabel(radio)}...`);
      const result = await requireRuntimeApi(state).downloadSelectedRadio({
        sessionId: await ctx.session.idOf(session),
      });
      // Described from the result rather than the editor, which may hold
      // another codeplug by the time the outcome is reported (see below).
      const downloaded = codeplugParams({ currentRows: result.rows, codeplugSource: "radio" });
      if (ctx.session.isCurrent(session)) {
        state.currentRows = result.rows;
      }
      // The schema comes after the rows, and only now: the download just put
      // this radio's image on its session, and the runtime builds column
      // metadata from that image (get_radio_column_metadata in
      // web/python/webchirp_bridge/column_metadata.py). A driver whose
      // capabilities live in the codeplug -- Retevis RT98 publishes Low/Mid/High
      // loaded and only the PMR levels blank -- would otherwise leave the grid
      // on the boot-time blank schema, which lists neither the levels these
      // rows carry nor the ones the upload preflight would accept, and which
      // rewrites them the next time the radio is re-selected
      // (adoptPowerLevels in web/js/ui/channel-table.ts).
      await refreshMetadataForDownloadedRadio(session);
      // An image opened during the transfer took the editor over: its load
      // waits behind the transfer in the runtime queue and lands while the
      // schema above is fetched. The radio was read, so this is a success,
      // but the image's channels and settings stay where they are.
      if (!ctx.session.isCurrent(session)) {
        log.setStatus(
          `${makeModelLabel(radio)} download finished after another codeplug was opened; `
            + "its channels were not loaded.",
        );
        log.logDebug("DOWNLOAD discarded: the editor moved to another session while it ran.");
        trackCloneOutcome("radio_download_success", radio, startedAt, cloneTransport, downloaded);
        return;
      }
      state.currentHeaders = state.radioMetadata.headers?.length
        ? state.radioMetadata.headers
        : (result.headers || []);
      ctx.table.sortRowsByLocation();
      state.codeplugSource = "radio";
      ctx.settings.replaceState({
        supported: Array.isArray(result.settings) && result.settings.length > 0,
        available: Array.isArray(result.settings) && result.settings.length > 0,
        requiresImage: false,
        message: "",
        groups: ctx.settings.cloneGroups(result.settings || []),
      });
      ctx.table.clearInvalidHighlights();
      ctx.settings.clearInvalid();
      ctx.table.resetRowSelection();
      ctx.table.render();
      ctx.settings.updateViewButtons();
      ctx.settings.render();
      log.setStatus(
        `${makeModelLabel(radio)} download complete (${state.currentRows.length} channels).`
          + undecodedChannelsNote(result.unreadableChannels),
      );
      trackCloneOutcome("radio_download_success", radio, startedAt, cloneTransport, downloaded);
      if (result.ident) {
        log.logSerial(`IDENT ${result.ident}`);
      }
    } catch (error) {
      trackCloneOutcome("radio_download_failure", radio, startedAt, cloneTransport, cloneFailureParams(error, "transfer"));
      log.reportActionError("Download", error);
      log.logSerial(`ERROR ${errorSummary(error)}`);
    } finally {
      endCloneProgress();
      setCloneRunning(false);
    }
  }

  async function uploadToRadio() {
    // As in downloadFromRadio(): one transfer at a time.
    if (cloneRunning) {
      return;
    }
    if (!state.selectedRadio) {
      log.setStatus("Search for and select a radio first.");
      return;
    }
    // Captured for the same reason as in downloadFromRadio(): the selection can
    // change while the write is in flight, and every label, payload and event
    // below has to keep meaning the radio the upload started against.
    const radio = state.selectedRadio;
    const session = ctx.session.current();
    const startedAt = Date.now();
    // As is the transport, which a port lost mid-clone clears (handlePortLost).
    const cloneTransport = transport;
    // Distinguishes a codeplug CHIRP itself rejected from a transfer that
    // reached the radio and failed there.
    let stage = "preflight";
    setCloneRunning(true);
    try {
      trackRadioEvent("radio_upload", radio);
      log.setStatus("Running upload preflight validation...");
      const preflight = await runUploadPreflight(session);
      if (preflight.stale) {
        log.setStatus("Upload cancelled: the selected radio changed.");
        return;
      }
      if (!preflight.valid) {
        const count = Array.isArray(preflight.issues) ? preflight.issues.length : 0;
        const blockedColumn = firstIssueColumn(preflight.issues);
        trackRadioEvent("upload_blocked_preflight", radio, {
          ...codeplugParams(state),
          issue_count: count,
          first_column: blockedColumn,
        });
        recordFlow(FLOWS.RADIO_UPLOAD, OUTCOMES.BLOCKED, {
          ...radioEventParams(radio),
          ...codeplugParams(state),
          first_column: blockedColumn,
        });
        log.setStatus(`Upload blocked: ${preflight.summary || "preflight validation failed"}.`);
        return;
      }
      stage = "transfer";
      log.setStatus(`Uploading to ${makeModelLabel(radio)}...`);
      beginCloneProgress(`Uploading to ${makeModelLabel(radio)}...`);
      const uploadResult = await requireRuntimeApi(state).uploadSelectedRadio({
        sessionId: await ctx.session.idOf(session),
        rows: state.currentRows,
        settings: ctx.settings.getGroups(),
      });
      // The radio was written either way. The settings it reports back belong
      // in the editor only while it still holds the session written from,
      // not an image opened during the write.
      if (ctx.session.isCurrent(session)) {
        ctx.settings.setGroups(uploadResult.settings);
        ctx.settings.clearInvalid();
        ctx.settings.render();
      }
      log.setStatus(`${makeModelLabel(radio)} upload complete.`);
      // codeplug_source is the question this event exists to answer on the
      // write path: whether people upload what they just read off the radio,
      // or a file they brought with them.
      trackCloneOutcome("radio_upload_success", radio, startedAt, cloneTransport, codeplugParams(state));
    } catch (error) {
      // codeplugParams is merged in here as well as on the success and blocked
      // paths, or grouping upload outcomes by codeplug_source would count every
      // success and no failure at all. It is deliberately not done for a failed
      // *download*: there the editor still holds whatever preceded the
      // transfer, so the same dimensions would describe the wrong codeplug.
      trackCloneOutcome("radio_upload_failure", radio, startedAt, cloneTransport, {
        ...codeplugParams(state),
        ...cloneFailureParams(error, stage),
      });
      ctx.settings.markRefused(error);
      log.reportActionError("Upload", error);
      log.logSerial(`ERROR ${errorSummary(error)}`);
    } finally {
      endCloneProgress();
      setCloneRunning(false);
    }
  }

  function bindEvents() {
    // Each toggle returns its promise, which a browser ignores, so a test's
    // dispatch() can wait for the whole connect.
    dom.serialConnectToggleEl.addEventListener("click", () => (connected ? disconnectSerial() : connectSerial("auto")));
    dom.webusbConnectToggleEl.addEventListener("click", () => (connected ? disconnectSerial() : connectSerial("webusb")));
    dom.webbluetoothConnectToggleEl.addEventListener("click", () => (connected ? disconnectSerial() : connectSerial("webbluetooth")));

    dom.radioDownloadEl.addEventListener("click", () => {
      downloadFromRadio();
    });

    dom.radioUploadEl.addEventListener("click", () => {
      uploadToRadio();
    });

    dom.unsupportedBrowserContinueEl.addEventListener("click", () => {
      setBrowserUnsupportedOverlayVisible(false);
      log.logSerial("Continuing without serial support; radio download/upload stays unavailable.");
    });
  }

  return {
    bindEvents,
    setSerialController,
    setCloneSupported,
    isCloneRunning,
    handlePortLost,
    setSidebarControlsEnabled,
    setBrowserUnsupportedOverlayVisible,
    refreshSerialConnectToggleLabel,
    updateSerialActionState,
    updateCloneProgress,
    runUploadPreflight,
  };
}
